/**
 * MarkdownField — a reusable Write/Preview markdown textarea with a formatting
 * toolbar. Designed for use inside dialogs / forms.
 *
 * Mirrors the inline editor first built in BannerForm but extracted as a
 * standalone component so it can be shared across modules.
 */

import { useRef } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import TableChartIcon from '@mui/icons-material/TableChart';
import { MarkdownContent } from '@backstage/core-components';

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles()((theme) => ({
  wrap: {},
  label: {
    display: 'block',
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  labelError: {
    color: theme.palette.error.main,
  },
  box: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`,
    },
  },
  boxError: {
    borderColor: `${theme.palette.error.main} !important`,
    '&:focus-within': {
      boxShadow: `0 0 0 2px ${theme.palette.error.main}22`,
    },
  },
  tabsRow: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : theme.palette.grey[50],
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  tabs: { minHeight: 36 },
  tab: {
    minHeight: 36,
    minWidth: 72,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.5, 1.5),
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'nowrap' as const,
    overflowX: 'auto',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : theme.palette.grey[50],
    padding: theme.spacing(0.25, 0.75),
    boxSizing: 'border-box' as const,
  },
  toolbarDivider: {
    height: 18,
    margin: '0 3px',
  },
  toolbarBtn: {
    padding: 4,
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary },
  },
  headingBtn: {
    minWidth: 0,
    padding: '2px 6px',
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary, backgroundColor: 'transparent' },
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box' as const,
    border: 'none',
    outline: 'none',
    resize: 'vertical' as const,
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: 13,
    lineHeight: 1.6,
    padding: theme.spacing(1.5),
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  },
  preview: {
    padding: theme.spacing(1.5),
    fontSize: 14,
    lineHeight: 1.7,
    '& p': { margin: '0 0 8px 0' },
    '& p:last-child': { margin: 0 },
    '& a': { color: theme.palette.primary.main },
    '& strong, & b': { fontWeight: '700 !important' as any },
    '& em, & i': { fontStyle: 'italic !important' as any },
    '& del, & s': { opacity: 0.6 },
    '& h1, & h2, & h3, & h4': { marginTop: 12, marginBottom: 4, fontWeight: 700 },
    '& ul, & ol': { paddingLeft: 20, marginBottom: 8 },
    '& li': { marginBottom: 2 },
    '& code': {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontSize: '0.875em',
      padding: '1px 4px',
      borderRadius: 3,
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)',
    },
    '& pre': {
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      overflow: 'auto',
      marginBottom: 8,
    },
    '& pre code': { backgroundColor: 'transparent', padding: 0 },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: 0,
      marginBottom: 8,
      paddingLeft: theme.spacing(2),
      color: theme.palette.text.secondary,
    },
  },
  previewEmpty: {
    padding: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: 13,
  },
  helper: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  helperError: {
    color: theme.palette.error.main,
  },
}));

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MarkdownFieldProps {
  /** Field label */
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Tab index: 0 = Write, 1 = Preview */
  tab: 0 | 1;
  onTabChange: (tab: 0 | 1) => void;
  required?: boolean;
  /** Validation error message to show below the field */
  error?: string;
  placeholder?: string;
  /** Minimum height for the textarea and preview (px). Default: 140 */
  minHeight?: number;
  /** Additional helper text shown below when there is no error */
  helperText?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function applyInlineWrap(
  value: string,
  start: number,
  end: number,
  before: string,
  after: string,
  placeholder: string,
): { newValue: string; newStart: number; newEnd: number } {
  const selected = value.slice(start, end) || placeholder;
  const newValue = value.slice(0, start) + before + selected + after + value.slice(end);
  return { newValue, newStart: start + before.length, newEnd: start + before.length + selected.length };
}

// Adds or toggles a line prefix on every line in the selection (or current line)
function applyLinePrefix(
  value: string,
  start: number,
  end: number,
  prefix: string,
): { newValue: string; newStart: number; newEnd: number } {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  let lineEnd: number;
  if (end !== start) lineEnd = end;
  else { const nl = value.indexOf('\n', start); lineEnd = nl === -1 ? value.length : nl; }
  const block = value.substring(lineStart, lineEnd);
  const lines = block.split('\n');
  const allPrefixed = lines.every(l => l.startsWith(prefix));
  const newBlock = allPrefixed
    ? lines.map(l => l.substring(prefix.length)).join('\n')
    : lines.map(l => (l.startsWith(prefix) ? l : prefix + l)).join('\n');
  const delta = newBlock.length - block.length;
  return {
    newValue: value.substring(0, lineStart) + newBlock + value.substring(lineEnd),
    newStart: start + (allPrefixed ? -prefix.length : prefix.length),
    newEnd: end + delta,
  };
}

// Inserts a block snippet at the cursor, wrapped with blank lines
function insertBlock(
  value: string,
  start: number,
  snippet: string,
): { newValue: string; newStart: number; newEnd: number } {
  const needsBefore = start > 0 && value[start - 1] !== '\n';
  const prefix = needsBefore ? '\n\n' : '\n';
  const insertion = `${prefix}${snippet}\n`;
  const newValue = `${value.substring(0, start)}${insertion}${value.substring(start)}`;
  const cursorPos = start + insertion.length;
  return { newValue, newStart: cursorPos, newEnd: cursorPos };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function MarkdownField({
  label,
  value,
  onChange,
  tab,
  onTabChange,
  required,
  error,
  placeholder,
  minHeight = 140,
  helperText,
}: MarkdownFieldProps) {
  const { classes } = useStyles();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const applyWrap = (before: string, after: string, ph: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const { newValue, newStart, newEnd } = applyInlineWrap(value, el.selectionStart, el.selectionEnd, before, after, ph);
    onChange(newValue);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newStart, newEnd);
    });
  };

  const applyPrefix = (prefix: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const { newValue, newStart, newEnd } = applyLinePrefix(value, el.selectionStart, el.selectionEnd, prefix);
    onChange(newValue);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newStart, newEnd);
    });
  };

  const applyBlock = (snippet: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const { newValue, newStart, newEnd } = insertBlock(value, el.selectionStart, snippet);
    onChange(newValue);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newStart, newEnd);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const el = textareaRef.current;
    if (!el) return;
    const { selectionStart: start, selectionEnd: end } = el;

    if (start === end) {
      // No selection — insert two spaces at the cursor (Tab) or do nothing (Shift+Tab)
      if (e.shiftKey) return;
      const newValue = `${value.substring(0, start)}  ${value.substring(start)}`;
      onChange(newValue);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + 2, start + 2);
      });
    } else {
      // Multi-line selection — indent (Tab) or dedent (Shift+Tab) every selected line
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const block = value.substring(lineStart, end);
      const lines = block.split('\n');

      let newBlock: string;
      if (e.shiftKey) {
        newBlock = lines.map(l => {
          if (l.startsWith('  ')) return l.slice(2);
          if (l.startsWith(' ')) return l.slice(1);
          return l;
        }).join('\n');
      } else {
        newBlock = lines.map(l => `  ${l}`).join('\n');
      }

      const delta = newBlock.length - block.length;
      const newValue = value.substring(0, lineStart) + newBlock + value.substring(end);
      // Keep the same selection range, adjusted for the indentation delta
      const newStart = e.shiftKey
        ? Math.max(lineStart, start - (lines[0].length - newBlock.split('\n')[0].length))
        : start + 2;
      onChange(newValue);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(newStart, end + delta);
      });
    }
  };

  const defaultHelper = `Supports Markdown — **bold**, _italic_, \`code\`, lists, links, etc.${required ? '' : ''}`;

  const renderBody = () => {
    if (tab === 0) {
      return (
        <textarea
          ref={textareaRef}
          className={classes.textarea}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? 'Write your answer in Markdown\u2026'}
          spellCheck
          style={{ minHeight }}
        />
      );
    }
    if (value.trim()) {
      return (
        <div className={classes.preview} style={{ minHeight }}>
          <MarkdownContent content={value} dialect="gfm" />
        </div>
      );
    }
    return <div className={classes.previewEmpty} style={{ minHeight }}>Nothing to preview yet.</div>;
  };

  return (
    <div className={classes.wrap}>
      <Typography
        component="label"
        className={`${classes.label} ${error ? classes.labelError : ''}`}
      >
        {label}{required && ' *'}
      </Typography>

      <div className={`${classes.box} ${error ? classes.boxError : ''}`}>
        {/* Row 1: Write / Preview tabs */}
        <div className={classes.tabsRow}>
          <Tabs
            value={tab}
            onChange={(_e, v) => onTabChange(v)}
            indicatorColor="primary"
            textColor="primary"
            className={classes.tabs}
          >
            <Tab label="Write" className={classes.tab} />
            <Tab label="Preview" className={classes.tab} />
          </Tabs>
        </div>

        {/* Row 2: formatting toolbar — only shown on Write tab */}
        {tab === 0 && (
          <div className={classes.toolbarRow}>
              {/* Headings */}
              <Tooltip title="Heading 1">
                <Button className={classes.headingBtn} onMouseDown={e => { e.preventDefault(); applyPrefix('# '); }}>H1</Button>
              </Tooltip>
              <Tooltip title="Heading 2">
                <Button className={classes.headingBtn} onMouseDown={e => { e.preventDefault(); applyPrefix('## '); }}>H2</Button>
              </Tooltip>
              <Tooltip title="Heading 3">
                <Button className={classes.headingBtn} onMouseDown={e => { e.preventDefault(); applyPrefix('### '); }}>H3</Button>
              </Tooltip>

              <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

              {/* Inline formatting */}
              <Tooltip title="Bold">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyWrap('**', '**', 'bold text'); }}>
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italic">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyWrap('*', '*', 'italic text'); }}>
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Strikethrough">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyWrap('~~', '~~', 'strikethrough'); }}>
                  <FormatStrikethroughIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

              {/* Code */}
              <Tooltip title="Inline code">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyWrap('`', '`', 'code'); }}>
                  <CodeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Code block">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); const el = textareaRef.current; if (!el) return; const sel = value.slice(el.selectionStart, el.selectionEnd); applyBlock(`\`\`\`\n${sel || 'code here'}\n\`\`\``); }}>
                  <TerminalIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

              {/* Block elements */}
              <Tooltip title="Blockquote">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyPrefix('> '); }}>
                  <FormatQuoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bullet list">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyPrefix('- '); }}>
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Numbered list">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyPrefix('1. '); }}>
                  <FormatListNumberedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

              {/* Links & media */}
              <Tooltip title="Link">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); const el = textareaRef.current; if (!el) return; const sel = value.slice(el.selectionStart, el.selectionEnd); applyWrap('[', '](https://)', sel || 'link text'); }}>
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Image">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); const el = textareaRef.current; if (!el) return; const sel = value.slice(el.selectionStart, el.selectionEnd); applyWrap('![', '](https://)', sel || 'alt text'); }}>
                  <ImageIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />

              {/* Misc */}
              <Tooltip title="Horizontal rule">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyBlock('---'); }}>
                  <HorizontalRuleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Table">
                <IconButton size="small" className={classes.toolbarBtn} onMouseDown={e => { e.preventDefault(); applyBlock('| Header 1 | Header 2 | Header 3 |\n| --- | --- | --- |\n| Cell 1 | Cell 2 | Cell 3 |\n| Cell 4 | Cell 5 | Cell 6 |'); }}>
                  <TableChartIcon fontSize="small" />
                </IconButton>
              </Tooltip>
          </div>
        )}

        {/* Body: textarea or preview */}
        {renderBody()}
      </div>

      <Typography component="p" className={`${classes.helper} ${error ? classes.helperError : ''}`}>
        {error ?? helperText ?? defaultHelper}
      </Typography>
    </div>
  );
}
